import { useNavigate } from 'react-router-dom';
import { Color } from '../constants';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-400 hover:text-gray-600 mb-6 inline-flex items-center gap-1"
          >
            ← Back
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: Color.NAVY }}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: Color.NAVY }}>
                Privacy Policy
              </h1>
              <p className="text-xs text-gray-400">Work Orders App · Effective June 1, 2026</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-8 text-sm text-gray-700 leading-relaxed">

          <section>
            <p>
              This Privacy Policy describes how <strong>A.G.</strong> ("we", "us", or "our")
              collects, uses, and protects information in connection with the <strong>Work Orders App</strong> (the "Application").
              The Application is an internal business tool provided to authorized employees only.
              By using the Application, you acknowledge this policy.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              1. Who This Policy Applies To
            </h2>
            <p>
              This policy applies to all authorized users of the Work Orders App — including Account Sales Managers,
              District Sales Managers, General Sales Managers, Sales Activators, Sales Trainees, and Administrators.
              Access is granted by invitation only. There is no public registration.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              2. Information We Collect
            </h2>
            <p className="mb-3">We collect only the information necessary to operate the Application:</p>
            <ul className="space-y-2 pl-4">
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Account information:</strong> your full name, work email address, phone number, and assigned role and route.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Work order activity:</strong> work orders you create, are assigned to, or complete — including notes, dates, task categories, and status changes.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Photos:</strong> images you upload in connection with work orders.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Authentication logs:</strong> records of login events (email, timestamp) for security purposes.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Audit logs:</strong> records of actions taken within the Application (e.g. who changed what and when).</span>
              </li>
            </ul>
            <p className="mt-3">
              We do not collect passwords. Authentication uses a one-time code sent to your work email.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              3. How We Use Your Information
            </h2>
            <p className="mb-3">Information is used solely to operate and maintain the Application:</p>
            <ul className="space-y-2 pl-4">
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>To authenticate your identity and provide access to the Application.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>To create, assign, track, and complete work orders.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>To send operational email notifications (e.g. login codes, overdue order alerts).</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>To maintain audit logs for accountability and operational oversight.</span>
              </li>
            </ul>
            <p className="mt-3">
              Your information is not used for advertising, profiling, or any purpose outside of business operations.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              4. Who Can See Your Information
            </h2>
            <p className="mb-3">
              Access to your information within the Application is role-based:
            </p>
            <ul className="space-y-2 pl-4">
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>Your manager can see work orders you create or are assigned to.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>Administrators can access staff records and system logs.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>No other users have access to your personal information.</span>
              </li>
            </ul>
            <p className="mt-3">
              Your information is <strong>not shared with any third party</strong>, sold, or disclosed outside
              of authorized users, except as required by applicable law.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              5. Where Your Data Is Stored
            </h2>
            <p className="mb-3">
              The Application uses the following infrastructure, all located in or routed through North American data centres:
            </p>
            <ul className="space-y-2 pl-4">
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Backend & database:</strong> Render.com (Portland, Oregon, USA)</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Frontend & photos:</strong> Cloudflare (global CDN, data processed in North America)</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span><strong>Email delivery:</strong> Resend.com</span>
              </li>
            </ul>
            <p className="mt-3">
              All data is transmitted over encrypted connections (HTTPS/TLS).
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              6. Data Retention
            </h2>
            <p>
              Work order records, audit logs, and account information are retained for as long as they are
              needed for operational purposes. When an employee is deactivated, their account is disabled
              but records they created are retained for audit and continuity purposes.
              You may request deletion of your personal information by contacting us (see Section 8).
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              7. Your Rights Under PIPEDA
            </h2>
            <p className="mb-3">
              As a Canadian resident, you have rights under the <em>Personal Information Protection and
              Electronic Documents Act</em> (PIPEDA), including the right to:
            </p>
            <ul className="space-y-2 pl-4">
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>Know what personal information we hold about you.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>Request correction of inaccurate information.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>Request deletion of your personal information, subject to legal and operational requirements.</span>
              </li>
              <li className="flex gap-2">
                <span style={{ color: Color.LIGHT_BLUE }}>•</span>
                <span>Withdraw consent, where consent is the basis for processing.</span>
              </li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us using the information in Section 8.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              8. Contact
            </h2>
            <p>
              If you have questions about this policy or wish to exercise your privacy rights, contact:
            </p>
            <div
              className="mt-3 p-4 rounded-xl"
              style={{ backgroundColor: '#F0F4FF' }}
            >
              <p className="font-medium" style={{ color: Color.NAVY }}>A.G.</p>
              <p className="text-gray-500 text-xs mt-0.5">Operator, Work Orders App</p>
              <a
                href="mailto:support@mail.rbcd.app"
                className="text-sm mt-1 inline-block"
                style={{ color: Color.LIGHT_BLUE }}
              >
                support@mail.rbcd.app
              </a>
            </div>
          </section>

          <section>
            <h2 className="font-semibold text-base mb-3" style={{ color: Color.NAVY }}>
              9. Changes to This Policy
            </h2>
            <p>
              We may update this policy from time to time. The effective date at the top of this page
              will reflect the most recent revision. Continued use of the Application after any changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <div className="pt-4 border-t border-gray-100 text-xs text-gray-400 text-center">
            Work Orders App · support@mail.rbcd.app · © {new Date().getFullYear()} A.G.
          </div>

        </div>
      </div>
    </div>
  );
}